import { useInfiniteQuery } from "react-query";
import type { UseInfiniteQueryOptions } from "react-query";

import { useApiClient } from "api/useApiClient";

import type { BroadcastFilters, BroadcastMessagesResponse, GetBroadcastMessagesArgs } from "../schemas/messaging";

const queryKeyPrefix = "getBroadcastMessages";
type QueryKey = [typeof queryKeyPrefix, number, number?, BroadcastFilters?];
const getBroadcastMessagesQueryKey = (args: GetBroadcastMessagesArgs): QueryKey => [
  queryKeyPrefix,
  args.userID,
  args.before,
  args.filters,
];

export const useGetBroadcastMessages = (
  args: GetBroadcastMessagesArgs,
  options?: Omit<
    UseInfiniteQueryOptions<
      BroadcastMessagesResponse,
      Error,
      BroadcastMessagesResponse,
      BroadcastMessagesResponse,
      QueryKey
    >,
    "queryKey" | "queryFn" | "getNextPageParam"
  >
) => {
  const client = useApiClient();
  const limit = args.limit ?? 15;

  return useInfiniteQuery({
    queryKey: getBroadcastMessagesQueryKey(args),
    queryFn: ({ pageParam }) =>
      client.getBroadcastMessages({
        before: pageParam,
        filters: args.filters,
        limit,
        userID: args.userID,
      }),
    getNextPageParam: (lastPage: BroadcastMessagesResponse) => {
      // If we got fewer messages than the limit, we've reached the end
      if (lastPage.length < limit) {
        return undefined;
      }
      // Return the ID of the oldest message (last in the array) to use as the 'before' cursor
      const oldestMessage = lastPage[lastPage.length - 1];
      return oldestMessage?.id;
    },
    ...options,
  });
};
